import { useDeferredValue, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import type { AssetStatus } from "@torre/assets";

import { Badge, Button, EmptyState, Input, Select, Table, type TableColumn } from "../../../components/ui";
import { AssetFormModal } from "../components/AssetFormModal";
import { SummaryCards } from "../components/SummaryCards";
import { ErrorState, InlineSpinner, Loader, SectionFrame } from "../components/shared";
import { useAssets, useAssetsSummary, useCanSeeCosts, usePeople } from "../hooks/useActivos";
import { assetPath } from "../lib/conflicts";
import { formatDateTime, formatMoney } from "../lib/format";
import { ASSET_STATUS_TONES, categoryLabel, categoryOptions, langOf, statusLabel, statusOptions, toneOf } from "../lib/vocab";
import type { AssetFilters, AssetRow } from "../services/activosApi";

// ============================================================================
// Inventario de Activos: resumen, filtros (categoría, estado, asignado,
// búsqueda por serie o nombre) y tabla. Archivo común, idéntico en CC-CONSOLE
// y WAYAK. La columna de costo sólo existe con `feat:activos.costos` (D6).
// ============================================================================

/** A quién o dónde está asignado, en una línea (la unidad y la base sólo se referencian). */
export function assignmentText(a: AssetRow["assignment"] | null | undefined, labels: { vehicle: string; base: string }): string {
  if (!a) return "—";
  const parts = [a.custodian?.name || a.custodian?.email, a.location, a.vehicleId ? labels.vehicle : null, a.baseId ? labels.base : null].filter(Boolean);
  return parts.length ? parts.join(" · ") : "—";
}

export default function InventarioSection() {
  const { t, i18n } = useTranslation("activos");
  const lang = langOf(i18n.language);
  const navigate = useNavigate();
  const canSeeCosts = useCanSeeCosts();
  const summary = useAssetsSummary();
  const people = usePeople();

  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [status, setStatus] = useState("");
  const [custodianId, setCustodianId] = useState("");
  const [creating, setCreating] = useState(false);
  const search = useDeferredValue(q.trim());

  const filters = useMemo<AssetFilters>(
    () => ({ q: search || undefined, category: category || undefined, status: (status || undefined) as AssetStatus | undefined, custodianId: custodianId || undefined }),
    [search, category, status, custodianId],
  );
  const hasFilters = Boolean(search || category || status || custodianId);
  const list = useAssets(filters);
  const rows = useMemo(() => (list.data?.pages || []).flatMap((p) => p.items || []), [list.data]);

  const columns: TableColumn<AssetRow>[] = [
    {
      key: "name",
      header: t("fields.name"),
      render: (a) => (
        <div className="min-w-0">
          <p className="truncate font-medium text-content">{a.name}</p>
          <p className="truncate text-xs text-content-muted">{[a.brand, a.model].filter(Boolean).join(" · ") || "—"}</p>
        </div>
      ),
    },
    { key: "category", header: t("fields.category"), render: (a) => categoryLabel(a.category, lang) },
    { key: "serial", header: t("fields.serial"), hideOnMobile: true, render: (a) => a.serial || "—" },
    {
      key: "status",
      header: t("fields.status"),
      render: (a) => <Badge tone={toneOf(ASSET_STATUS_TONES, a.status)}>{statusLabel(a.status, lang)}</Badge>,
    },
    { key: "assigned", header: t("fields.assignedTo"), hideOnMobile: true, render: (a) => assignmentText(a.assignment, { vehicle: t("fields.vehicle"), base: t("fields.base") }) },
    ...(canSeeCosts
      ? [
          {
            key: "cost",
            header: t("fields.unitCost"),
            align: "right" as const,
            render: (a: AssetRow) => formatMoney(a.purchase?.unitCost, a.purchase?.currency, lang),
          },
        ]
      : []),
    { key: "updated", header: t("fields.updatedAt"), hideOnMobile: true, render: (a) => formatDateTime(a.updatedAt, lang) },
  ];

  const none = { value: "", label: t("filters.all") };
  const filtersBar = (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="inventario-filters">
      <Input
        aria-label={t("filters.search")}
        placeholder={t("filters.search")}
        value={q}
        onChange={(e) => setQ(e.target.value)}
        icon={<i className="ri-search-line" />}
        data-testid="filter-q"
      />
      <Select aria-label={t("fields.category")} value={category} onChange={(e) => setCategory(e.target.value)} options={[{ ...none, label: t("filters.allCategories") }, ...categoryOptions(lang)]} data-testid="filter-category" />
      <Select aria-label={t("fields.status")} value={status} onChange={(e) => setStatus(e.target.value)} options={[{ ...none, label: t("filters.allStatuses") }, ...statusOptions(lang)]} data-testid="filter-status" />
      <Select
        aria-label={t("fields.assignedTo")}
        value={custodianId}
        onChange={(e) => setCustodianId(e.target.value)}
        options={[{ ...none, label: t("filters.anyone") }, ...(people.data || []).map((p) => ({ value: p.id, label: p.name }))]}
        data-testid="filter-custodian"
      />
    </div>
  );

  return (
    <SectionFrame
      title={t("inventario.title")}
      subtitle={t("inventario.subtitle")}
      actions={
        <Button size="sm" icon={<i className="ri-add-line" />} onClick={() => setCreating(true)} data-testid="asset-create">
          {t("inventario.create")}
        </Button>
      }
      filters={filtersBar}
    >
      {summary.data ? <SummaryCards summary={summary.data} canSeeCosts={canSeeCosts} /> : null}

      {list.isLoading ? (
        <Loader />
      ) : list.isError ? (
        <ErrorState error={list.error} onRetry={() => void list.refetch()} />
      ) : rows.length === 0 ? (
        hasFilters ? (
          <EmptyState icon="ri-filter-off-line" title={t("inventario.noResults.title")} description={t("inventario.noResults.text")} />
        ) : (
          <EmptyState
            icon="ri-stack-line"
            title={t("inventario.empty.title")}
            description={t("inventario.empty.text")}
            action={
              <Button size="sm" icon={<i className="ri-add-line" />} onClick={() => setCreating(true)}>
                {t("inventario.create")}
              </Button>
            }
          />
        )
      ) : (
        <>
          <Table columns={columns} rows={rows} rowKey={(a) => a.id} onRowClick={(a) => navigate(assetPath(a.id))} />
          {list.hasNextPage ? (
            <div className="flex justify-center">
              <Button size="sm" variant="secondary" onClick={() => void list.fetchNextPage()} disabled={list.isFetchingNextPage}>
                {list.isFetchingNextPage ? <InlineSpinner /> : t("common.loadMore")}
              </Button>
            </div>
          ) : null}
        </>
      )}

      {creating ? (
        <AssetFormModal canSeeCosts={canSeeCosts} onClose={() => setCreating(false)} onSaved={(a) => navigate(assetPath(a.id))} />
      ) : null}
    </SectionFrame>
  );
}
